/**
 * Empty / clear claims must be derived from a complete measure.
 *
 * Sixth consecutive round of the same shape (R17O-1 → PGS18-2 → R18O-3/4 →
 * fix-r19c helpers → R20O-2/4 → fix-r21f → R21C-3/R21C-2): a surface asserted
 * a state it did not measure. Hand-rolling "is this empty" per page is how it
 * recurs.
 *
 * ## RETRACTION (fix-r22f / R21C-3 + v6ns R21C-2)
 *
 * `fix-r21f` claimed `MeasuredSet<T>` makes a half-measured collection
 * *unrepresentable* — "the structural fix." Two independent remediation lenses
 * rejected that claim. They were right.
 *
 * In TypeScript here, completeness is an **epistemic** property of how the
 * data was gathered (fan-out settled? list cap fired?), not a structural
 * property of the array. The caller still supplies `measureComplete`. Anyone
 * can write `measuredSetOf(rows, true)` after a failed fan-out, or paint
 * `set.items.length === 0 ? emptyCopy : rows` and ignore the flag. Half-measured
 * remains a value. The splitting partition the type was supposed to forbid
 * still compiles — see `measured-claim.test.ts` ("splitting partition still
 * writable").
 *
 * **What this module actually is:** a convention plus helpers. The helpers
 * refuse to emit clear/empty copy when `measureComplete` is false. Call sites
 * that route through them cannot invent a quiet empty from an incomplete
 * measure *unless they lie at construction or bypass the helpers*. That is
 * enforcement by discipline and review, not by making the bad state
 * unrepresentable.
 *
 * **Do not copy this as a structure.** Lanes asked whether truncated listings
 * or partial payloads can be made unrepresentable should read this retraction
 * first: if the "completeness" bit is still caller-supplied, you have a
 * convention. Prefer an honest convention + check over a third patch that
 * re-labels the same object as structural.
 *
 * What TypeScript *can* do here (and what we keep): force clear/empty copy
 * through helpers that consult the flag; refuse `?? clearClaim` soft-restore
 * fallbacks; type painters to take `MeasuredSet` so stripping the measure is
 * visible in the signature. What it cannot do without a proof from the I/O
 * itself: stop a lying `measureComplete: true`.
 *
 * Mirror: when measureComplete is true and the set is empty, return emptyCopy
 * quietly — no hedging that trains the operator to ignore emptiness.
 */

/**
 * A collection bundled with a caller-supplied completeness flag.
 *
 * Incomplete measures are valid values — clear claims derived from them are
 * not. The flag is not proof; it is a declaration the helpers honour.
 */
export type MeasuredSet<T> = Readonly<{
  items: readonly T[];
  measureComplete: boolean;
}>;

/** Build a MeasuredSet. Incomplete measures are valid values — clear claims are not. */
export function measuredSetOf<T>(
  items: readonly T[],
  measureComplete: boolean,
): MeasuredSet<T> {
  return { items, measureComplete };
}

/**
 * True when a known server list LIMIT has not fired.
 * Responses with no `has_more` treat `length >= limit` as truncation.
 */
export function measureCompleteForListCap(
  itemCount: number,
  listLimit: number,
): boolean {
  return itemCount < listLimit;
}

export type MeasuredClaimArgs = {
  /** How many items the surface successfully collected. */
  itemCount: number;
  /** False when any required fan-out member failed or has not settled. */
  measureComplete: boolean;
  /** Honest empty string when the full set was read and contained nothing. */
  emptyCopy: string;
  /** String when itemCount is 0 but the measure is incomplete. */
  incompleteEmptyCopy: string;
};

/**
 * Copy for an empty-state slot, or null when the list is non-empty (caller
 * paints rows). Never returns emptyCopy while measureComplete is false.
 */
export function measuredEmptyCopy(args: MeasuredClaimArgs): string | null {
  if (args.itemCount > 0) return null;
  if (!args.measureComplete) return args.incompleteEmptyCopy;
  return args.emptyCopy;
}

/** Empty-state copy derived from a MeasuredSet (preferred entry for list pages). */
export function measuredEmptyCopyFromSet(
  set: MeasuredSet<unknown>,
  copies: { emptyCopy: string; incompleteEmptyCopy: string },
): string | null {
  return measuredEmptyCopy({
    itemCount: set.items.length,
    measureComplete: set.measureComplete,
    emptyCopy: copies.emptyCopy,
    incompleteEmptyCopy: copies.incompleteEmptyCopy,
  });
}

/**
 * Headline when the work stack is clear. Same contract as
 * {@link measuredEmptyCopy}: incomplete measure → incompleteCopy; complete
 * empty → clearCopy. Null when work remains (caller paints the work headline).
 */
export function measuredClearHeadline(args: {
  workCount: number;
  measureComplete: boolean;
  clearCopy: string;
  incompleteCopy: string;
}): string | null {
  if (args.workCount > 0) return null;
  if (!args.measureComplete) return args.incompleteCopy;
  return args.clearCopy;
}

/** Clear headline derived from a MeasuredSet work stack. */
export function measuredClearHeadlineFromSet(
  set: MeasuredSet<unknown>,
  copies: { clearCopy: string; incompleteCopy: string },
): string | null {
  return measuredClearHeadline({
    workCount: set.items.length,
    measureComplete: set.measureComplete,
    clearCopy: copies.clearCopy,
    incompleteCopy: copies.incompleteCopy,
  });
}

/**
 * Completeness-claim population derived from admin-portal/src (fix-r22f).
 *
 * Surfaces that assert a quiet empty, a clear stack, a fleet total, or
 * "nothing here" from a collection that can be incomplete. Not every empty
 * string in the portal — only claims whose truth depends on having finished
 * measuring.
 *
 * | Surface | Claim | Measure today |
 * |---------|-------|---------------|
 * | Home awareness | "All caught up." | MeasuredSet + measureComplete |
 * | Home row pending | "0 pending" / counts | queueMeasureComplete |
 * | Home scope empty | "No companies in this scope." | scope members (local) |
 * | Waiting empty | "No questions are waiting…" | MeasuredSet (fan-out + cap) |
 * | Today headline | "All caught up." / "Nothing needs you." | measuredClearHeadline + Waiting count |
 * | Health | "No companies registered." | getFleetHealth list (all companies, not active-only) |
 * | Shell picker | "No companies available" | listCompanies settled empty |
 * | Receipt shelf / archive | "Empty." / shelf empty | single GET (cap residual → fix-r22b) |
 * | Groups / rules / tokens / reports / GL / txns / balances | local empty copy | usually single complete GET |
 *
 * A population derived beats a type applied: new clear claims belong on this
 * list, routed through the helpers, not through a bare `.length === 0`.
 */
export const COMPLETENESS_CLAIM_SITES = [
  "home.ts:formatAwarenessLine — All caught up.",
  "home.ts:pendingLabel — 0 pending / counts",
  "home.ts:renderHomePage — No companies in this scope.",
  "waiting.ts:paintList — No questions are waiting on anyone else.",
  "queue.ts:setHeadlineFromSurface — All caught up. / Nothing needs you.",
  "health.ts — No companies registered.",
  "main.ts — No companies available",
  "receipt-shelf.ts — shelf / archive Empty.",
] as const;

// ---------------------------------------------------------------------------
// The all-months search's own words (B0-2)
// ---------------------------------------------------------------------------

/**
 * The words Activity and Books use for a search across every month, in ONE
 * place. Both pages wrote the same eight sentences, so deleting one page's copy
 * made the other page's duplicate-sentence count move (the cross-group seam B0
 * removes). This is a MOVE, not an edit: every word and every order is the
 * same, and the templates that interpolate the months searched stay in the
 * pages (`Searched every month (${from} to ${to}).`), because only the tail is
 * shared.
 */
export const SEARCH_ALL_MONTHS_PROMPT = "Type something to search all months.";
export const SEARCH_OLDER_NOT_SEARCHED = "Older months were not searched.";
export const SEARCHED_EVERY_MONTH = "Searched every month.";
export const NOT_EVERY_MONTH_SEARCHED = "Not every month was searched.";
export const SEARCH_NEWEST_MATCHES =
  "Showing the newest 500 matches. Add more words to see older ones.";
export const NO_MATCHES_ANY_MONTH = "No matches in any month.";
export const NO_MATCHES_MONTHS_SEARCHED = "No matches in the months searched.";
export const SEARCH_COULD_NOT_FINISH = "Search could not finish. Try again.";

/**
 * Rows a read could not return, in words: `3 months could not be read.` The
 * caller supplies the count words (already plural and formatted), so the
 * sentence's tail has one owner. Activity, Books and Check now share it.
 */
export function couldNotBeReadText(countWords: string): string {
  return `${countWords} could not be read.`;
}
